import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Repeat, Sprout, TimerReset } from 'lucide-react';
import {
  MembershipDashboard,
  fetchMembershipDashboard,
  runMembershipExpiryJob,
  seedMembershipPlans,
} from '../../../services/MasterAdmin/membershipService';
import MembershipPanelShell from './MembershipPanelShell';
import { MembershipError, MembershipLoading, MembershipSuccess } from './MembershipAlerts';
import { MEMBERSHIP_BASE_PATH, msPrimaryBtn, msSecondaryBtn } from './membershipUi';

const MembershipDashboardPanel: React.FC = () => {
  const [dashboard, setDashboard] = useState<MembershipDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setDashboard(await fetchMembershipDashboard());
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const cards = dashboard
    ? [
        { label: 'Active', value: dashboard.activeCount, href: `${MEMBERSHIP_BASE_PATH}/members` },
        { label: 'Expired', value: dashboard.expiredCount, href: `${MEMBERSHIP_BASE_PATH}/members` },
        { label: 'Cancelled', value: dashboard.cancelledCount, href: `${MEMBERSHIP_BASE_PATH}/members` },
        { label: 'Pending transfers', value: dashboard.pendingTransfers, href: `${MEMBERSHIP_BASE_PATH}/transfers` },
        {
          label: 'Month revenue',
          value: `₹${Number(dashboard.monthRevenue || 0).toLocaleString()}`,
          href: `${MEMBERSHIP_BASE_PATH}/reports`,
        },
      ]
    : [];

  return (
    <MembershipPanelShell title="Overview">
      <MembershipError message={error} />
      <MembershipSuccess message={success} />
      {loading ? <MembershipLoading /> : null}

      {!loading && dashboard ? (
        <div className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
            {cards.map((card) => (
              <Link
                key={card.label}
                to={card.href}
                className="rounded-xl border border-gray-200 bg-white p-4 transition-colors hover:border-emerald-300 dark:border-gray-700 dark:bg-gray-800"
              >
                <p className="text-xs font-medium uppercase tracking-wide text-gray-400">{card.label}</p>
                <p className="mt-1 text-2xl font-bold text-gray-900 dark:text-white">{card.value}</p>
              </Link>
            ))}
          </div>

          {dashboard.planCounts?.length ? (
            <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Active by plan</h3>
              <div className="mt-3 grid gap-3 sm:grid-cols-3">
                {dashboard.planCounts.map((row, idx) => (
                  <div key={`${row.planName || 'plan'}-${idx}`} className="rounded-lg bg-gray-50 px-3 py-2 dark:bg-gray-900">
                    <div className="text-xs text-gray-500">{row.planName || 'Unknown'}</div>
                    <div className="text-lg font-semibold text-gray-900 dark:text-white">{row.count ?? 0}</div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <Link
              to={`${MEMBERSHIP_BASE_PATH}/plans`}
              className="flex items-center justify-between rounded-xl border border-gray-200 px-4 py-3 text-sm font-medium text-gray-800 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-100 dark:hover:bg-gray-800"
            >
              Manage plans
              <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to={`${MEMBERSHIP_BASE_PATH}/members`}
              className="flex items-center justify-between rounded-xl border border-gray-200 px-4 py-3 text-sm font-medium text-gray-800 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-100 dark:hover:bg-gray-800"
            >
              User memberships
              <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to={`${MEMBERSHIP_BASE_PATH}/transfers`}
              className="flex items-center justify-between rounded-xl border border-gray-200 px-4 py-3 text-sm font-medium text-gray-800 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-100 dark:hover:bg-gray-800"
            >
              Review transfers
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={busy}
              className={msPrimaryBtn}
              onClick={async () => {
                try {
                  setBusy(true);
                  await seedMembershipPlans();
                  setSuccess('Default Silver, Gold, and Platinum plans seeded.');
                  await load();
                } catch (e: any) {
                  setError(e?.response?.data?.message || 'Failed to seed plans');
                } finally {
                  setBusy(false);
                }
              }}
            >
              <Sprout className="mr-2 h-4 w-4" />
              Seed default plans
            </button>
            <button
              type="button"
              disabled={busy}
              className={msSecondaryBtn}
              onClick={async () => {
                try {
                  setBusy(true);
                  const result = await runMembershipExpiryJob();
                  setSuccess(`Expiry job completed. ${JSON.stringify(result || {})}`);
                  await load();
                } catch (e: any) {
                  setError(e?.response?.data?.message || 'Expiry job failed');
                } finally {
                  setBusy(false);
                }
              }}
            >
              <TimerReset className="mr-2 h-4 w-4" />
              Run expiry job
            </button>
            <Link to={`${MEMBERSHIP_BASE_PATH}/tools`} className={msSecondaryBtn}>
              <Repeat className="mr-2 h-4 w-4" />
              Open tools
            </Link>
          </div>
        </div>
      ) : null}
    </MembershipPanelShell>
  );
};

export default MembershipDashboardPanel;
